import {Set} from 'immutable';
import {ofType} from 'redux-observable';
import {of, concat, from, zip} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import moment from 'moment-timezone';
import i18next from 'i18next';

import {combineEpics} from 'web-app/util/redux-observable';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import * as ChildGroupQueries from 'web-app/react/profile/child-profile/group-move/queries';
import * as ChildApi from 'web-app/react/entities/children/api';
import * as EmployeeApi from 'web-app/react/entities/employees/api';

import * as Actions from './actions';
import {type PeoplePickerConfig, type SelectedPeople} from './types';

const addChildToGroup = (groupId: string, childId: string) => {
    const date = moment().format('YYYY-MM-DD');
    return from(
        getApolloClient().mutate({
            mutation: ChildGroupQueries.AddChildRoomMoveQuery,
            variables: {
                childId,
                groupId,
                date,
            },
        }),
    );
};

const addPeopleToGroup = (groupId: string, childIds: SelectedPeople, employeeIds: SelectedPeople) => {
    const childObservables = childIds.map(id => addChildToGroup(groupId, id));
    const employeeObservables = employeeIds.map(id => EmployeeApi.changeGroup(id, groupId));
    return zip(...childObservables, ...employeeObservables).pipe(
        map(() => GlobalEventActions.updateSuccess.action(i18next.t('group_tag.groupGeneralUpdated'))),
    );
};

const addPeopleToTag = (
    tagId: string,
    config: PeoplePickerConfig,
    childIds: SelectedPeople,
    employeeIds: SelectedPeople,
) => {
    const removedChildren = config.selectedChildren ? config.selectedChildren.subtract(childIds) : Set();
    const removedEmployees = config.selectedEmployees ? config.selectedEmployees.subtract(employeeIds) : Set();
    const addedChildren = childIds.subtract(config.selectedChildren || Set());
    const addedEmployees = employeeIds.subtract(config.selectedEmployees || Set());
    return zip(
        ...removedChildren.map(id => ChildApi.removeTag(id, tagId)),
        ...removedEmployees.map(id => EmployeeApi.removeTag(id, tagId)),
        ...addedChildren.map(id => ChildApi.addTag(id, tagId)),
        ...addedEmployees.map(id => EmployeeApi.assignTag(id, tagId)),
    ).pipe(map(() => GlobalEventActions.updateSuccess.action(i18next.t('group_tag.tagGeneralUpdated'))));
};

const getSubmitObservable = (config: PeoplePickerConfig, childIds: SelectedPeople, employeeIds: SelectedPeople) => {
    if (childIds.size === 0 && employeeIds.size === 0) {
        return null;
    } else if (config.submitConfig && 'groupId' in config.submitConfig) {
        return addPeopleToGroup(config.submitConfig.groupId, childIds, employeeIds);
    } else if (config.submitConfig && 'tagId' in config.submitConfig) {
        return addPeopleToTag(config.submitConfig.tagId, config, childIds, employeeIds);
    } else {
        return null;
    }
};

const submit = action$ =>
    action$.pipe(
        ofType(Actions.submit.type),
        mergeMap((action: ReturnType<typeof Actions.submit.action>) => {
            const {payload} = action;

            const {config, selectedChildren, selectedEmployees} = payload;

            const submitObservable = getSubmitObservable(config, selectedChildren, selectedEmployees);

            if (!submitObservable) {
                return of(Actions.submitAborted.action());
            }

            return concat(submitObservable, of(Actions.submitSuccess.action())).pipe(
                catchError(e => of(Actions.submitFailed.action(e))),
            );
        }),
    );

export default combineEpics(submit);
